import { IconChevronDown } from '@tabler/icons-react';
import {
  ActionIcon,
  Button,
  Group,
  Menu,
  useMantineTheme,
} from '@mantine/core';
import classes from './SplitButton.module.css';

interface SplitButtonProps {
  mainAction: {
    label: string;
    onClick: () => void;
    leftSection?: React.ReactNode;
  };
  menuItems: Array<{
    label: string;
    onClick: () => void;
    leftSection?: React.ReactNode;
    disabled?: boolean;
  }>;
  style?: React.CSSProperties;
  disabled?: boolean;
  loading?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  /**
   * Portal the menu dropdown to the body (default true). Pass false when the
   * button renders inside a mobile bottom sheet, where body portals get
   * mispositioned and grow the mobile layout viewport.
   */
  menuWithinPortal?: boolean;
  'data-tour'?: string;
}

// Map Mantine Button sizes → pixel heights
const buttonHeights: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number> = {
  xs: 28,
  sm: 36,
  md: 42,
  lg: 50,
  xl: 60,
};

export function SplitButton({
  mainAction,
  menuItems,
  style,
  disabled,
  loading,
  size = 'sm',
  fullWidth,
  menuWithinPortal = true,
  'data-tour': dataTour,
}: SplitButtonProps) {
  const theme = useMantineTheme();
  const height = buttonHeights[size];

  return (
    <Group wrap="nowrap" gap={0} style={{ width: fullWidth ? '100%' : 'auto' }} data-tour={dataTour}>
      <Button
        radius="xl"
        className={classes.button}
        onClick={(e) => {
          e.stopPropagation();
          mainAction.onClick();
        }}
        style={{ ...style, flex: fullWidth ? 1 : 'none' }}
        disabled={disabled}
        loading={loading}
        size={size}
        variant="filled"
        loaderProps={{
          color: disabled ? 'dimmed' : 'white',
          size: 'sm',
        }}
        styles={{
          loader: {
            position: 'absolute',
            left: 'calc(50% + 6px)',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          },
        }}
        leftSection={mainAction.leftSection}
      >
        {mainAction.label}
      </Button>

      <Menu transitionProps={{ transition: 'pop' }} position="bottom-end" withinPortal={menuWithinPortal}>
        <Menu.Target>
          <ActionIcon
            radius="xl"
            className={classes.menuControl}
            variant="filled"
            color={theme.primaryColor}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            style={{
              height, // match Button height
              width: height,
              backgroundColor: style?.backgroundColor,
              ...(!disabled && { backgroundColor: style?.backgroundColor }),
            }}
          >
            <IconChevronDown size={16} stroke={1.5} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {menuItems.map((item, index) => (
            <Menu.Item
              key={index}
              leftSection={item.leftSection}
              onClick={(e) => {
                e.stopPropagation();
                item.onClick();
              }}
              disabled={item.disabled}
            >
              {item.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}

